import { Injectable } from '@angular/core';
import type { TranslocoMissingHandler, TranslocoMissingHandlerData } from '@jsverse/transloco';

import { frenchSoFar } from '../shared/i18n/french-catalogue';
import { directionOf } from '../shared/i18n/language-registry';

const readPath = (catalogue: object, key: string): unknown =>
  key.split('.').reduce<unknown>((node, segment) => {
    if (node === null || typeof node !== 'object') return undefined;
    return (node as Record<string, unknown>)[segment];
  }, catalogue);

/**
 * A left-to-right sentence dropped into a right-to-left page loses its trailing punctuation: the
 * full stop is directionally neutral, so the bidi algorithm hands it to the paragraph and renders
 * it at the far left. Wrapping the fallback in a first-strong isolate settles its direction from
 * its own first letter and seals it off from the Arabic around it.
 */
const FIRST_STRONG_ISOLATE = '⁨';
const POP_DIRECTIONAL_ISOLATE = '⁩';

/**
 * French is the source of truth: the only complete catalogue, and the only one whose religious
 * wording has been validated. A key missing from English or Arabic falls back to its French
 * string rather than painting `private.onboarding.createTitle` across the screen — which is what
 * Transloco's default handler does, and what makes a partial catalogue unshippable.
 */
@Injectable()
export class FrenchFallbackMissingHandler implements TranslocoMissingHandler {
  handle(key: string, data: TranslocoMissingHandlerData): string {
    const french = readPath(frenchSoFar(), key);
    if (typeof french === 'string') {
      return directionOf(data.activeLang) === 'rtl'
        ? `${FIRST_STRONG_ISOLATE}${french}${POP_DIRECTIONAL_ISOLATE}`
        : french;
    }

    // Absent from French too: that is a bug in the code, not an untranslated string. The key is
    // the most useful thing to show a developer, and there is nothing better to show anyone else.
    if (data.missingHandler.logMissingKey && !data.prodMode) {
      console.warn(`Missing translation for '${key}' in every catalogue, French included.`);
    }
    return key;
  }
}
